import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageHero } from '@/components/shared/PageHero';
import { SectionHeader } from '@/components/shared/SectionHeader';
import { BARANGAY, BRAND } from '@/data/mockData';
import { MapPin, Users, Home as HomeIcon, Layers, Target, Eye, Heart, History } from 'lucide-react';

const VISION = 'A safe, progressive, and people-centered barangay where every resident has equitable access to services, opportunities, and a clean, peaceful environment.';
const MISSION = 'To deliver fast and transparent public service, foster community participation, protect public order, promote health and livelihood, and modernize barangay governance through technology and accountability.';

const VALUES = [
  { icon: Heart, title: 'Malasakit', desc: 'Genuine care and empathy in every interaction with our kababayan.' },
  { icon: Users, title: 'Pakikipagkapwa', desc: 'Service that respects and uplifts the dignity of every resident.' },
  { icon: Eye, title: 'Transparency', desc: 'Open books, open doors, and open conversations.' },
  { icon: Target, title: 'Accountability', desc: 'Clear responsibilities and measurable outcomes for every program.' },
];

const PUROKS = [
  { id: 1, name: 'Purok 1', area: 'San Roque side', leader: 'Mr. Antonio Garcia' },
  { id: 2, name: 'Purok 2', area: 'A. Bonifacio Ave (north)', leader: 'Ms. Helen Domingo' },
  { id: 3, name: 'Purok 3', area: 'A. Bonifacio Ave (south)', leader: 'Mr. Jaime Ramos' },
  { id: 4, name: 'Purok 4', area: 'Rizal St. corner', leader: 'Ms. Linda Gonzales' },
  { id: 5, name: 'Purok 5', area: 'Sitio Masipag', leader: 'Mr. Eduardo Cruz' },
  { id: 6, name: 'Purok 6', area: 'Sitio Mabuhay', leader: 'Ms. Teresa Aquino' },
  { id: 7, name: 'Purok 7', area: 'Sitio Masikap', leader: 'Mr. Renato Vergara' },
];

const About = () => {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Us"
        title={`Get to know ${BARANGAY.name}`}
        subtitle={`A vibrant community of ${BARANGAY.population} residents across ${BARANGAY.puroks} puroks in ${BARANGAY.municipality}, ${BARANGAY.province}.`}
        breadcrumbs={[{ label: 'About' }]}
      />

      {/* HISTORY + IMAGE */}
      <section className="container-app section-padding">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-lg-soft">
            <img src={BRAND.communityHeroUrl} alt="San Isidro community" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 to-transparent" />
          </div>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
              <History className="h-3.5 w-3.5" /> History
            </div>
            <h2 className="mt-3 font-display text-3xl lg:text-4xl font-bold tracking-tight">
              A community shaped by <span className="gradient-text">resilience and bayanihan</span>
            </h2>
            <p className="mt-4 text-foreground/80 leading-relaxed">
              Officially established in {BARANGAY.founded}, {BARANGAY.name} has grown from a quiet residential cluster
              into a thriving barangay of over {BARANGAY.population} residents. Through every milestone — typhoons,
              pandemic, modernization — our community has stood together with the spirit of bayanihan that defines
              every Filipino neighborhood.
            </p>
            <p className="mt-3 text-foreground/80 leading-relaxed">
              Today, the barangay government is investing in modern services, transparent governance, and digital tools
              that bring the barangay closer to every resident — wherever they are.
            </p>
            <div className="mt-6 grid grid-cols-3 gap-4">
              <Tile icon={Users} label="Residents" value={BARANGAY.population} />
              <Tile icon={HomeIcon} label="Households" value={BARANGAY.households} />
              <Tile icon={Layers} label="Puroks" value={BARANGAY.puroks} />
            </div>
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <section className="bg-secondary/40">
        <div className="container-app section-padding grid md:grid-cols-2 gap-6">
          <Card className="p-8 border-0 card-elevated bg-gradient-card">
            <Eye className="h-7 w-7 text-primary" />
            <h3 className="mt-3 font-display text-2xl font-bold text-primary-deep">Our Vision</h3>
            <p className="mt-3 text-foreground/80 leading-relaxed">{VISION}</p>
          </Card>
          <Card className="p-8 border-0 card-elevated bg-gradient-card">
            <Target className="h-7 w-7 text-accent" />
            <h3 className="mt-3 font-display text-2xl font-bold text-primary-deep">Our Mission</h3>
            <p className="mt-3 text-foreground/80 leading-relaxed">{MISSION}</p>
          </Card>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="container-app section-padding">
        <SectionHeader eyebrow="Our Values" title="Core values that guide our service" align="center" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VALUES.map((v) => (
            <Card key={v.title} className="p-6 text-center card-elevated border-0">
              <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-primary text-primary-foreground">
                <v.icon className="h-5 w-5" />
              </div>
              <h4 className="mt-3 font-heading font-semibold">{v.title}</h4>
              <p className="mt-1.5 text-sm text-muted-foreground">{v.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* PUROKS */}
      <section className="container-app section-padding">
        <SectionHeader
          eyebrow="The Community"
          title="Our 7 Puroks"
          description="Each purok has its own community leaders working hand-in-hand with the barangay to address local needs."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PUROKS.map((p) => (
            <Card key={p.id} className="p-5 card-elevated border-0">
              <Badge className="bg-accent-soft text-accent hover:bg-accent-soft">{p.name}</Badge>
              <div className="mt-3 font-heading font-semibold">{p.area}</div>
              <div className="mt-1 text-xs text-muted-foreground">Purok Leader · {p.leader}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* LOCATION */}
      <section className="container-app section-padding">
        <SectionHeader eyebrow="Visit Us" title="Where to find the Barangay Hall" />
        <Card className="overflow-hidden border-0 shadow-md-soft">
          <div className="grid lg:grid-cols-5">
            <div className="lg:col-span-2 p-8 bg-gradient-card">
              <MapPin className="h-7 w-7 text-primary" />
              <h3 className="mt-3 font-display text-xl font-bold">{BARANGAY.name} Hall</h3>
              <p className="mt-2 text-sm/relaxed text-foreground/80">{BARANGAY.address}</p>
              <div className="mt-5 space-y-2 text-sm">
                <div><span className="text-muted-foreground">Office Hours:</span> {BARANGAY.officeHours}</div>
                <div><span className="text-muted-foreground">Saturday:</span> {BARANGAY.saturdayHours}</div>
                <div><span className="text-muted-foreground">Hotline:</span> {BARANGAY.hotline}</div>
                <div><span className="text-muted-foreground">Email:</span> {BARANGAY.email}</div>
              </div>
            </div>
            <div className="lg:col-span-3 min-h-[300px] bg-secondary relative">
              <iframe
                title="Barangay San Isidro location"
                src="https://www.openstreetmap.org/export/embed.html?bbox=121.118%2C14.578%2C121.143%2C14.598&layer=mapnik&marker=14.588%2C121.130"
                className="absolute inset-0 h-full w-full"
                loading="lazy"
              />
            </div>
          </div>
        </Card>
      </section>
    </div>
  );
};

const Tile = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border bg-card p-4 text-center">
    <Icon className="h-4 w-4 mx-auto text-primary" />
    <div className="mt-2 font-display text-xl font-bold">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
  </div>
);

export default About;
